/**
 * D-EZ-109 — RIVALRY WATCH: the hottest pairs, what changed this week, and
 * what is riding on next week between them.
 *
 * Read from the rivalry book (D-EZ-108) as it stood after race N, and again
 * after race N−1 to say what moved: a new feud on the board, a pair boiling
 * over, one cooling off. Every pair still racing shares the track next week —
 * there is one race a week — so instead of "they will meet" the Wire says what
 * is at stake between them in the standings, and what happened the last time
 * they raced at next week's track.
 */

import { hottestRivalries, pairKey, rivalryBetween, STATE_WORDS, type RivalryMoment, type RivalryRecord, type RivalryStateName } from '../people/rivalry.ts';
import { shortTrack } from '../paper/fallbackPaper.ts';
import { stableHash } from '../story/hometown.ts';
import { num, say, type WireLine } from './line.ts';
import type { WeekContext } from './week.ts';

export type RivalryChange = 'new' | 'hotter' | 'cooler' | 'steady';

export interface WireRivalry {
  a: string;
  b: string;
  aName: string;
  bName: string;
  state: RivalryStateName;
  stateWord: string;
  prevState: RivalryStateName | null;
  change: RivalryChange;
  /** A short tag for how it moved this week ("New this week", "Hotter — was simmering"), or null for a quiet one. */
  badge: WireLine | null;
  /** What happened between them this week, or the last time it did. */
  lines: WireLine[];
}

const ORDER: Record<RivalryStateName, number> = { boiling: 5, hot: 4, simmering: 3, cooled: 2, 'buried-the-hatchet': 1, dormant: 0 };

const T = {
  new: ['New this week'],
  hotter: ['Hotter — was {was}'],
  cooler: ['Cooling — was {was}'],
  again: ['At it again'],
  thisWeek: [
    'At {track}: {moment}',
    'This week at {track}: {moment}',
    'What happened at {track}: {moment}',
    'The latest, at {track}: {moment}',
    'Sunday at {track}: {moment}',
  ],
  lastTime: [
    'Last run-in, {when}: {moment}',
    'The last time it flared, {when}: {moment}',
    'Most recently, {when}: {moment}',
    'The last word, {when}: {moment}',
    'Last time out, {when}: {moment}',
  ],
  firstSpark: [
    'How it started, {when}: {moment}',
    'It goes back to {when}: {moment}',
    'The first spark, {when}: {moment}',
    'Where it began, {when}: {moment}',
    'The start of it, {when}: {moment}',
  ],
  worst: [
    'The worst of it, {when}: {moment}',
    'The one neither forgets, {when}: {moment}',
    'Low point, {when}: {moment}',
    'The ugliest moment, {when}: {moment}',
    'The one that still stings, {when}: {moment}',
  ],
  atNext: [
    'The last time they raced at {track}: {moment}',
    'At {track} before: {moment}',
    'Last time at {track}: {moment}',
    'Their history at {track}: {moment}',
    '{track} remembers: {moment}',
  ],
  h2h: [
    '{leader} has finished ahead in {ahead} of their {races} races together.',
    'Head to head: {leader} {ahead}, {trailer} {behind}.',
    'On the track, {leader} has the edge — ahead {ahead} times to {behind}.',
    'Of {races} races together, {leader} has beaten {trailer} in {ahead}.',
    '{leader} leads the head-to-head, {ahead} to {behind}.',
  ],
  h2hEven: ['Dead even head to head: {ahead} each.', 'Neither has the edge: {ahead} apiece when both finish.'],
  stakeReg: [
    '{a} is {ra} in points, {b} {rb} — {gap} apart.',
    'In the standings: {a} {ra}, {b} {rb}, {gap} points between them.',
    'Points: {a} {ra}, {b} {rb}. {gap} in it.',
    '{gap} points separate them: {a} {ra}, {b} {rb}.',
    '{a} sits {ra}, {b} {rb} — a {gap}-point gap.',
  ],
  stakeRegSame: ['They are level on points.'],
  bothIn: [
    'Both are inside the playoff cut with {left} to go.',
    'Both would make the playoff if it started today.',
    'Both sit inside the cut, for now.',
    'Both are in the playoff picture today.',
    'Neither is sweating the cut — yet.',
  ],
  oneIn: [
    '{inName} is inside the playoff cut; {outName} is {gap} points short.',
    '{outName} sits {gap} points outside the cut; {inName} is in.',
    '{inName} is in the playoff picture, {outName} {gap} points out of it.',
    'Only {inName} would make the playoff today; {outName} needs {gap} points.',
    '{outName} is {gap} short of the cut. {inName} is in.',
  ],
  neitherIn: [
    'Neither is inside the playoff cut right now.',
    'Neither would make the playoff if it started today.',
    'Both are outside the cut looking in.',
    'No playoff spot for either one today.',
    'Both need points to reach the cut.',
  ],
  bothAlive: [
    'Both still alive in the {round}: {a} {ra}, {b} {rb} on round points.',
    'Both alive in the {round} — round points {a} {pa}, {b} {pb}.',
    'Two contenders: in the {round}, {a} has {pa} points, {b} {pb}.',
    'Both still chasing the title — {a} {ra}, {b} {rb} in the {round}.',
    'The {round} has them both: {a} {pa}, {b} {pb} so far.',
  ],
  oneAlive: [
    'Only {inName} is still alive for the title.',
    '{inName} is still in the hunt for the title; {outName} is racing for pride.',
    '{outName} is out of the title hunt. {inName} is not.',
    'One contender, one spoiler: {inName} is alive, {outName} is not.',
    '{inName} still has a title to race for; {outName} has a grudge.',
  ],
  neitherAlive: [
    'Neither is alive for the title now — this one is for pride.',
    'No title on the line for either — just the grudge.',
    'Both are out of the title hunt. Nothing to lose.',
    'Pride is all that is left between them.',
    'No championship at stake here; that may make it worse.',
  ],
} as const;

function when(w: WeekContext, m: RivalryMoment): string {
  if (m.seasonNumber === w.seasonNumber) return w.raceNumber - m.raceNumber === 1 ? 'last week' : m.raceNumber === 0 ? 'over the winter' : `at ${shortTrack(m.trackName)}`;
  if (m.seasonNumber === w.seasonNumber - 1) return `last season at ${shortTrack(m.trackName)}`;
  return `at ${shortTrack(m.trackName)}`;
}

/** The moment in a list that added the most heat, latest first on a tie. */
function biggest(ms: readonly RivalryMoment[]): RivalryMoment | null {
  let best: RivalryMoment | null = null;
  for (const m of ms) if (!best || m.heat >= best.heat) best = m;
  return best;
}

export function rivalryWatch(w: WeekContext, max = 3): WireRivalry[] {
  const book = w.people.book;
  const thisWeek = book.momentsByRace.get(`${w.seasonNumber}|${w.raceNumber}`) ?? [];
  const racing = (r: RivalryRecord) => r.bothActive && !w.lost.has(r.a) && !w.lost.has(r.b);
  const pool = hottestRivalries(book, 20).filter(racing);
  // Pairs that touched this week jump the queue; then the hottest of the rest
  // is always on the page; the other places rotate through the next few, so
  // the same three quiet feuds do not fill the column every week.
  const touched = new Set(thisWeek.map((m) => (m.aggressorId && m.victimId ? pairKey(m.aggressorId, m.victimId) : '')));
  const hit = pool.filter((r) => touched.has(pairKey(r.a, r.b)));
  const quiet = pool.filter((r) => !touched.has(pairKey(r.a, r.b)));
  const picked = hit.slice(0, max);
  if (picked.length < max && quiet[0]) picked.push(quiet[0]);
  const bench = quiet.slice(1, 6);
  for (let i = 0; picked.length < max && i < bench.length; i++) picked.push(bench[(w.raceNumber + i) % bench.length]);
  picked.sort((x, y) => ORDER[y.state] - ORDER[x.state] || y.heat - x.heat);

  return picked.map((r) => {
    const prev = w.peoplePrev ? rivalryBetween(w.peoplePrev.book, r.a, r.b) : null;
    const prevState = prev?.state ?? null;
    const was = prevState ?? 'dormant';
    const change: RivalryChange =
      ORDER[was] < ORDER.simmering ? 'new' : ORDER[r.state] > ORDER[was] ? 'hotter' : ORDER[r.state] < ORDER[was] ? 'cooler' : 'steady';
    const key = `${w.seasonKey}|${pairKey(r.a, r.b)}`;
    const occ = w.raceNumber;
    const ids = [r.a, r.b];
    const pairMoments = r.moments.filter((m) => m.kind !== 'hatchet');
    const mine = thisWeek.filter((m) => m.aggressorId && m.victimId && pairKey(m.aggressorId, m.victimId) === pairKey(r.a, r.b) && m.kind !== 'hatchet');
    const tone = change !== 'steady' ? change : mine.length > 0 ? 'again' : null;
    const badge = tone ? say(`badge-${tone}`, T[tone], { was: STATE_WORDS[was].toLowerCase() }, { key, driverIds: ids }) : null;

    const lines: WireLine[] = [];
    const now = biggest(mine);
    if (now) {
      const l = say('rivalry-this-week', T.thisWeek, { track: w.trackShort, moment: now.text }, { key, occurrence: occ, driverIds: ids });
      if (l) lines.push(l);
    } else {
      // A quiet week: one memory, taking turns between the last run-in, how it
      // started and the worst of it, so the column does not reprint one line.
      const last = pairMoments[pairMoments.length - 1] ?? null;
      const memories = [
        last ? { kind: 'rivalry-last-time', t: T.lastTime, m: last } : null,
        r.firstSpark && r.firstSpark !== last ? { kind: 'rivalry-first-spark', t: T.firstSpark, m: r.firstSpark } : null,
        r.worst && r.worst !== last && r.worst !== r.firstSpark ? { kind: 'rivalry-worst', t: T.worst, m: r.worst } : null,
      ].filter((x): x is NonNullable<typeof x> => x !== null);
      const pick = memories[(occ + (stableHash(key) % 3)) % Math.max(1, memories.length)];
      if (pick) {
        const l = say(pick.kind, pick.t, { when: when(w, pick.m), moment: pick.m.text }, { key, occurrence: occ, driverIds: ids });
        if (l) lines.push(l);
      }
    }

    // What rides on next week between them.
    const stake = stakeLine(w, r, key, occ);
    if (stake) lines.push(stake);

    // The last time they raced at next week's track.
    const venue = w.next?.venue ?? w.next?.trackId ?? null;
    if (venue && w.next) {
      const there = [...pairMoments].reverse().find((m) => m.venue === venue && m !== now);
      if (there) {
        const l = say('rivalry-at-next', T.atNext, { track: shortTrack(w.next.trackName), moment: there.text }, { key, occurrence: occ, driverIds: ids });
        if (l) lines.push(l);
      }
    }

    // Head to head, when it says something.
    const h = r.headToHead;
    // Every other week is plenty for the head-to-head.
    if (h.races >= 4 && lines.length < 3 && (occ + (stableHash(key) % 2)) % 2 === 0) {
      const aAhead = h.aAhead;
      const bAhead = h.bAhead;
      const lead = aAhead >= bAhead ? r.a : r.b;
      const l =
        aAhead === bAhead
          ? say('rivalry-h2h-even', T.h2hEven, { ahead: num(aAhead) }, { key, occurrence: occ, driverIds: ids })
          : say(
              'rivalry-h2h',
              T.h2h,
              {
                leader: w.short(lead),
                trailer: w.short(lead === r.a ? r.b : r.a),
                ahead: num(Math.max(aAhead, bAhead)),
                behind: num(Math.min(aAhead, bAhead)),
                races: num(h.races),
              },
              { key, occurrence: Math.floor(occ / 2), driverIds: ids },
            );
      if (l) lines.push(l);
    }

    return {
      a: r.a,
      b: r.b,
      aName: w.nameOf(r.a),
      bName: w.nameOf(r.b),
      state: r.state,
      stateWord: STATE_WORDS[r.state],
      prevState,
      change,
      badge,
      lines,
    };
  });
}

function stakeLine(w: WeekContext, r: RivalryRecord, key: string, occ: number): WireLine | null {
  const A = w.table.byDriver[r.a];
  const B = w.table.byDriver[r.b];
  if (!A || !B) return null;
  const p = w.picture;
  const ids = [r.a, r.b];
  if (p && p.phase === 'playoff') {
    const aIn = p.inIds.has(r.a);
    const bIn = p.inIds.has(r.b);
    if (aIn && bIn) {
      const ra = p.order.indexOf(r.a) + 1;
      const rb = p.order.indexOf(r.b) + 1;
      return say(
        'stake-both-alive',
        T.bothAlive,
        {
          round: p.round.label,
          a: w.short(r.a),
          b: w.short(r.b),
          ra: num(ra, 'ord'),
          rb: num(rb, 'ord'),
          pa: num(p.roundPoints.get(r.a) ?? 0),
          pb: num(p.roundPoints.get(r.b) ?? 0),
        },
        { key, occurrence: occ, driverIds: ids },
      );
    }
    if (aIn || bIn) {
      return say('stake-one-alive', T.oneAlive, { inName: w.short(aIn ? r.a : r.b), outName: w.short(aIn ? r.b : r.a) }, { key, occurrence: occ, driverIds: ids });
    }
    return say('stake-none-alive', T.neitherAlive, {}, { key, occurrence: occ, driverIds: ids });
  }
  const lines: string[] = [];
  const reg =
    A.points === B.points
      ? say('stake-level', T.stakeRegSame, {}, { key, occurrence: occ, driverIds: ids })
      : say(
          'stake-points',
          T.stakeReg,
          {
            a: w.short(A.rank < B.rank ? r.a : r.b),
            b: w.short(A.rank < B.rank ? r.b : r.a),
            ra: num(Math.min(A.rank, B.rank), 'ord'),
            rb: num(Math.max(A.rank, B.rank), 'ord'),
            gap: num(Math.abs(A.points - B.points)),
          },
          { key, occurrence: occ, driverIds: ids },
        );
  if (p && p.phase === 'regular' && p.cut && reg) {
    const aIn = p.inIds.has(r.a);
    const bIn = p.inIds.has(r.b);
    const cutLine =
      aIn && bIn
        ? say('stake-both-in', T.bothIn, { left: num(p.racesToCut, 'word') }, { key, occurrence: occ, driverIds: ids })
        : aIn || bIn
          ? say(
              'stake-one-in',
              T.oneIn,
              {
                inName: w.short(aIn ? r.a : r.b),
                outName: w.short(aIn ? r.b : r.a),
                gap: num(Math.max(0, p.cut.points - (w.table.byDriver[aIn ? r.b : r.a]?.points ?? 0))),
              },
              { key, occurrence: occ, driverIds: ids },
            )
          : say('stake-none-in', T.neitherIn, {}, { key, occurrence: occ, driverIds: ids });
    if (cutLine && w.raceNumber >= 10) {
      // Two facts, one line: the points and the cut.
      lines.push(reg.text, cutLine.text);
      return {
        ...reg,
        kind: `${reg.kind}+${cutLine.kind}`,
        template: `${reg.template} ${cutLine.template}`,
        text: lines.join(' '),
        names: { ...reg.names, ...cutLine.names },
        nums: { ...reg.nums, ...Object.fromEntries(Object.entries(cutLine.nums).map(([k, v]) => [`cut_${k}`, v])) },
      };
    }
  }
  return reg;
}
